/**
 * WaveVariantPicker — pure, no Phaser import (must remain node-testable).
 *
 * Given a stage's variant roster and the current wave context, picks a
 * variant assignment (baseId → variantId) for the wave such that no two
 * adjacent waves share an identical mix for any archetype that has ≥2
 * variants.
 */

import type { StageVariantRoster } from '@/data/enemyVariants';

/** Maps archetype baseId → variantId selected for the current wave. */
export type WaveVariantAssignment = Record<string, string>;

/**
 * Pick variant assignments for the archetypes present in the current wave.
 *
 * Rules:
 * - If the roster has no entry for a baseId, that archetype is omitted
 *   (graceful — no dangling variant references).
 * - If the roster pool has exactly one variant, always pick it.
 * - If the pool has ≥2 variants, exclude the variant used in the previous
 *   wave (prevAssignment[baseId]) and pick deterministically via waveIndex.
 *
 * The picker is deterministic — no random state — so tests can assert
 * exact variant sequences.
 */
export function pickWaveVariants(
  uniqueBaseIds: string[],
  roster: StageVariantRoster,
  prevAssignment: WaveVariantAssignment,
  waveIndex: number,
): WaveVariantAssignment {
  const result: WaveVariantAssignment = {};

  for (const baseId of uniqueBaseIds) {
    const pool = roster[baseId];
    if (!pool || pool.length === 0) continue;

    if (pool.length === 1) {
      result[baseId] = pool[0].variantId;
      continue;
    }

    const prevVarId = prevAssignment[baseId];
    // Exclude the previous variant so adjacent waves always differ.
    const candidates = pool.filter((v) => v.variantId !== prevVarId);
    // Deterministic selection: (waveIndex % candidates.length)
    result[baseId] = candidates[waveIndex % candidates.length].variantId;
  }

  return result;
}

/**
 * Extract unique baseIds from an array of enemy groups.
 * Convenience helper — keeps WaveManager integration tidy.
 */
export function extractBaseIds(enemyGroups: ReadonlyArray<{ id: string }>): string[] {
  const seen = new Set<string>();
  for (const g of enemyGroups) seen.add(g.id);
  return [...seen];
}
